import { SiteUrls } from '@tableau/shared-urls';
import { CatalogUrlParamKeys } from '@tableau/shared-urls/lib/src/SiteUrls';
import { assertUnreachable } from '@tableau/vizportalclient-common-components';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { FieldsReferencedByFieldQueryVariables, FieldsReferencedByFieldQuery, FieldsReferencedByFieldNodesFragment, FieldsReferencedByFieldNodesDatasourceFragment , FieldsReferencedByFieldDocument } from '../../../../../gen-src/queries';
import {
  ConnectionListResult,
  PaginatedQueryWrapper
} from '../../../data-catalog-shared';
import { LinksDisplayGrid, LinksDisplayGridRowData } from '../sharedDisplayGrid/LinksDisplayGrid';

interface FieldsReferencedByFieldGridRowData extends LinksDisplayGridRowData {
  readonly publishedDatasourceVizportalUrlId: string | null;
  readonly embeddedDatasourceNodeId: string | null;
  readonly datasourceName: string | null;
  readonly workbookVizportalUrlId: string | null;
}

type QueryVariables = FieldsReferencedByFieldQueryVariables;
type QueryResults = FieldsReferencedByFieldQuery;
type QueryResultListNode = FieldsReferencedByFieldNodesFragment;
type GridRowData = FieldsReferencedByFieldGridRowData;

function selectConnectionListResult(resultData: FieldsReferencedByFieldQuery): ConnectionListResult<QueryResultListNode> | null {
  const field = resultData && resultData.fields && resultData.fields[0];
  switch (field.__typename) {
    case 'BinField':
    case 'CalculatedField':
    case 'CombinedField':
    case 'CombinedSetField':
    case 'GroupField':
    case 'HierarchyField':
    case 'SetField':
      return field && field.fieldsConnection;
    default:
      return null;
  }
}

// TODO: jschneidereit verify
function mapResultNode(node: QueryResultListNode): GridRowData {
  const { id, name } = node;

  const datasource: FieldsReferencedByFieldNodesDatasourceFragment | null = node.datasource;

  const rest: GridRowData = {
    id, name, datasourceName: null, embeddedDatasourceNodeId: null, publishedDatasourceVizportalUrlId: null, workbookVizportalUrlId: null,
  }

  if (!datasource) {
    return rest;
  }

  const datasourceName = datasource.name;

  switch (datasource.__typename) {
    case 'EmbeddedDatasource': return {
      ...rest,
      datasourceName,
      embeddedDatasourceNodeId: datasource.id,
      workbookVizportalUrlId: datasource.workbook?.vizportalUrlId ?? null,
    }
    case 'PublishedDatasource': return {
      ...rest,
      datasourceName,
      publishedDatasourceVizportalUrlId: datasource.vizportalUrlId,
    }
    default:
      assertUnreachable(datasource)
      return rest;
  }

  // const rest: GridRowData = {
  //   id,
  //   name,
  //   datasourceName: datasource?.name ?? null,
  //   embeddedDatasourceNodeId: datasource?.__typename === 'EmbeddedDatasource' ? datasource.id : null
  // }


  // const publishedDatasource = datasource && datasource.__typename === 'PublishedDatasource' ? datasource : null;
  // const embeddedDatasource = datasource && datasource.__typename === 'EmbeddedDatasource' ? datasource : null;
  // const workbook = embeddedDatasource ? embeddedDatasource.workbook : null;
  // return {
  //   id: node.id,
  //   name: node.name,
  //   publishedDatasourceVizportalUrlId: publishedDatasource ? publishedDatasource.vizportalUrlId : null,
  //   embeddedDatasourceNodeId: embeddedDatasource ? embeddedDatasource.id : null,
  //   datasourceName: datasource ? datasource.name : null,
  //   workbookVizportalUrlId: datasource && 'workbook' in datasource ? datasource.workbook?.vizportalUrlId : null,
  // };
}

function urlForLink(urls: SiteUrls, rowData: FieldsReferencedByFieldGridRowData): string | null {
  if (rowData.workbookVizportalUrlId && rowData.embeddedDatasourceNodeId) {
    return urls.workbookLineageFieldPath(rowData.workbookVizportalUrlId, rowData.id) +
      filterToEmbeddedDatasource(rowData.embeddedDatasourceNodeId);
  } else if (rowData.publishedDatasourceVizportalUrlId) {
    return urls.datasourceLineageFieldPath(rowData.publishedDatasourceVizportalUrlId, rowData.id);
  }
  return null;
}

function urlForAnchorLink(urls: SiteUrls, rowData: FieldsReferencedByFieldGridRowData): string | null {
  if (rowData.workbookVizportalUrlId && rowData.embeddedDatasourceNodeId) {
    return urls.workbookLineageFieldUrl(rowData.workbookVizportalUrlId, rowData.id) +
      filterToEmbeddedDatasource(rowData.embeddedDatasourceNodeId);
  } else if (rowData.publishedDatasourceVizportalUrlId) {
    return urls.datasourceLineageFieldUrl(rowData.publishedDatasourceVizportalUrlId, rowData.id);
  }
  return null;
}

function filterToEmbeddedDatasource(datasourceId: string): string {
  return `?${CatalogUrlParamKeys.embeddedDataSource}=` + datasourceId;
}

function nameForLink(rowData: FieldsReferencedByFieldGridRowData): string | null {
  if (rowData.name === null || rowData.datasourceName === null) {
    return null;
  }
  return Messages.LinkNameWithParent({ name: rowData.name, parentName: rowData.datasourceName });
}

export type FieldsReferencedByFieldQueryGridProps = {
  readonly fieldId: string;
  readonly rowCount: number | null;
  readonly targetBlank?: boolean;
  readonly useAnchor?: boolean;
};

export function FieldsReferencedByFieldQueryGrid(props: FieldsReferencedByFieldQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyFieldsReferencedByFieldGridMessage();
  return (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={FieldsReferencedByFieldDocument}
      partialQueryVariables={{ fieldId: props.fieldId }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={mapResultNode}
    >
      {({ rowStore }) =>
        <LinksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          link={props.useAnchor ? urlForAnchorLink : urlForLink}
          name={nameForLink}
          targetBlank={props.targetBlank}
          useAnchor={props.useAnchor}
        />
      }
    </PaginatedQueryWrapper>
  );
}
